import dayjs from "dayjs";
import {
  getLastFiscalYearEnd,
  wibToday,
} from "../../../../../Utils/PeriodUtils";

// Shared by Umur Piutang & Hutang and Buku Besar Pembantu.

export const RECEIVABLE = "receivable";
export const PAYABLE = "payable";
export const REPORT_TYPES = [RECEIVABLE, PAYABLE];

/** The `type` query value, falling back to receivables for anything else. */
export const typeFromParam = (value) =>
  REPORT_TYPES.includes(value) ? value : RECEIVABLE;

// Age buckets in the order the API returns them, with the bar colour for each:
// grey for not yet due, then warming to red as it gets later. The colour only
// decorates; the legend carries the label and the amount.
export const BUCKET_COLORS = {
  current: "bg-slate-300",
  d1_30: "bg-amber-300",
  d31_60: "bg-orange-400",
  d61_90: "bg-orange-600",
  over_90: "bg-red-600",
};

export const OVERDUE_KEYS = ["d1_30", "d31_60", "d61_90", "over_90"];

const share = (value, total) => (total > 0 ? (value / total) * 100 : 0);

/**
 * What the summary card shows: the total, how much of it is past due (and
 * its share), the unused customer deposits, and one bar segment per bucket.
 * A zero bucket stays in the legend but draws no segment.
 */
export const summarizeAging = (aging) => {
  const totals = aging?.totals || {};
  const total = Number(aging?.total) || 0;
  const overdue = OVERDUE_KEYS.reduce(
    (sum, key) => sum + (Number(totals[key]) || 0),
    0,
  );
  const buckets = (aging?.buckets || []).map((bucket) => {
    const value = Number(totals[bucket.key]) || 0;
    return {
      key: bucket.key,
      label: bucket.label,
      value,
      percent: share(value, total),
      color: BUCKET_COLORS[bucket.key] || "bg-gray-300",
    };
  });
  return {
    total,
    overdue,
    overduePercent: Math.round(share(overdue, total)),
    deposit: Number(aging?.deposit) || 0,
    buckets,
  };
};

/** Whether the aging disagrees with the books by at least one sen. */
export const hasLedgerDifference = (aging) =>
  Math.abs(Number(aging?.difference) || 0) >= 0.005;

/**
 * The control accounts worth naming in the difference banner: the ones that
 * hold a balance, or the first one when all are zero.
 */
export const ledgerAccountsToName = (aging) => {
  const accounts = aging?.ledgerAccounts || [];
  const withBalance = accounts.filter(
    (account) => Math.abs(Number(account.balance) || 0) >= 0.005,
  );
  return withBalance.length ? withBalance : accounts.slice(0, 1);
};

/**
 * First day of the fiscal year that holds today (WIB), as a picker Date:
 * the day after the last fiscal year ended. Default start of the ledger
 * period.
 */
export const fiscalYearStart = (fiscalYearStartMonth, now = new Date()) =>
  dayjs(getLastFiscalYearEnd(fiscalYearStartMonth, now)).add(1, "day").toDate();

/** Default ledger period: start of this fiscal year to today. */
export const defaultLedgerRange = (fiscalYearStartMonth, now = new Date()) => [
  fiscalYearStart(fiscalYearStartMonth, now),
  wibToday(now),
];

/** Where a document number opens: the invoice in Pendapatan or Pengeluaran. */
export const documentPath = (type, id) =>
  `/v2/accounting/${type === PAYABLE ? "expenses" : "incomes"}?invoiceId=${encodeURIComponent(id)}`;

/**
 * What a ledger row is, worded like the exports: its type and own number,
 * then the invoice it settles ("Invoice 10002", "Pembayaran untuk 10001").
 * `settles(number)` words the last part in the UI language.
 */
export const entryText = (label, entry, settles) =>
  [label, entry?.number, entry?.documentNumber && settles(entry.documentNumber)]
    .filter(Boolean)
    .join(" ");
